/**
 * One I-tick of the monsters, in the order of the IRQ's sprite handler D_1CBD (slots 7 down to 2):
 * the drop-in (which alone runs while play is frozen, and under the clock too), the clock's freeze,
 * an angry monster's extra update, the start delay, then the AI of its kind - or of the escape fall
 * (state $0A), or of the power heart's freeze ($16, $17). Then the contact test, or on level 100 the
 * boss (D_1D84 runs in its place), and the Barons' passes due.
 */

import { angerLastMonster } from './anger';
import { attackerUpdate } from './attack';
import { banebouUpdate } from './banebou';
import { updateBarons } from './baron';
import { bossITick } from './boss';
import { huntedPlayer } from './chase';
import { touchPlayers } from './contact';
import { dropInTick, escapeTick, waitTick } from './dropIn';
import { monstaUpdate, pulpulUpdate } from './flier';
import { applyHeart, heartTick } from './heart';
import { invaderUpdate } from './invader';
import { syncFacing } from './motion';
import { ANGRY_FRAME_MASK, ANGRY_FRAME_PHASE } from './tuning';
import { walkerUpdate } from './walker';
import type { MonsterKind } from '../../levels/types';
import type { Monster, PlayerId, World } from '../types';

type KindAi = (world: World, m: Monster, target: PlayerId) => void;

/**
 * The state handlers of D_1E3A by kind. Hidegons, Drunk and Mighta run D_E9FD: their attack logic
 * D_EDCD, then the walker L_EA08 or the wind-up D_ED3B. The Invader's D_1E9F starts with its laser.
 */
const KIND_AI: Readonly<Record<MonsterKind, KindAi>> = {
  zenChan: walkerUpdate,
  hidegons: attackerUpdate,
  drunk: attackerUpdate,
  mighta: attackerUpdate,
  banebou: banebouUpdate,
  pulpul: (w, m) => pulpulUpdate(w, m),
  monsta: (w, m) => monstaUpdate(w, m),
  invader: invaderUpdate,
};

/** D_1E6C: one AI update, by the monster's state. */
function runAi(world: World, m: Monster, target: PlayerId): void {
  if (m.frozen) heartTick(world, m);
  else if (m.state === 'escaping') escapeTick(world, m);
  else KIND_AI[m.kind](world, m, target);
  syncFacing(m);
}

function monsterTick(world: World, m: Monster, frozen: boolean): void {
  const a = m.ai;
  if (m.state === 'trapped' || m.state === 'dead') {
    // Caught: L1090 clears the delay and the state ($B4 = 0: a heart's freeze too); the next escape is set up
    // afresh. A slot held for a wind-up is let go (the C64's $42 times out after 9 passes, D_13BE).
    a.delay = 0;
    a.escaping = false;
    a.reserved = false;
    a.heart = 0;
    m.frozen = false;
    return;
  }
  if (m.state === 'dropIn') {
    dropInTick(m, frozen);
    syncFacing(m);
    return;
  }
  if (frozen) return;
  // $1CF9: under the clock the handler skips every monster in states 1..$0A (not the heart's $16/$17).
  if (world.power.clock > 0 && !m.frozen) return;
  const target = huntedPlayer(world, m);
  // L_1D03: anger's extra update, before (and regardless of) the start delay.
  if (m.angry && (world.clock.frame & ANGRY_FRAME_MASK) === ANGRY_FRAME_PHASE) runAi(world, m, target);
  if (waitTick(m)) {
    syncFacing(m);
    return;
  }
  runAi(world, m, target);
}

/** One I-tick: every monster, then contact with the players (or the boss), then the Barons' passes due. */
export function monstersITick(world: World): void {
  const frozen = world.flow.freeze > 0;
  applyHeart(world);
  if (!frozen) angerLastMonster(world);
  for (let i = world.monsters.length - 1; i >= 0; i--) {
    const m = world.monsters[i];
    if (m) monsterTick(world, m, frozen);
  }
  if (world.boss) bossITick(world);
  else if (!frozen) touchPlayers(world);
  updateBarons(world);
}
